import { useMemo, useRef, useState } from 'react'
import type { EstoqueRow } from '../../hooks/useEstoqueAnalytics'
import KpiCard from './KpiCard'
import BarChart from './charts/BarChart'
import { fmtBRL, fmtPct, fmtNum } from '../../lib/format'
import { kpiMeta } from '../../lib/kpiMeta'

interface Props { rows: EstoqueRow[]; periodLabel: string }

// Giro mínimo para considerar item "ativo" (evita capital parado)
const LOW_TURNOVER_THRESHOLD = 0.5 // unidades/mês

export default function FinanceiroPanel({ rows, periodLabel }: Props) {
  const stats = useMemo(() => {
    let totalValue = 0        // Σ(stock × cost_avg)
    let totalMargin = 0       // Σ(margin_value)
    let totalRevenue = 0      // Σ(revenue_window)
    let capitalParado = 0     // Σ capital de itens com giro < threshold
    let capitalParadoCount = 0
    let roiSum = 0, roiCount = 0

    const abcFinanceiro: Record<string, { value: number; margin: number; revenue: number }> = {
      A: { value: 0, margin: 0, revenue: 0 },
      B: { value: 0, margin: 0, revenue: 0 },
      C: { value: 0, margin: 0, revenue: 0 },
    }

    for (const r of rows) {
      const stockVal = (r.current_stock ?? 0) * (r.cost_avg ?? 0)
      totalValue += stockVal
      if (r.margin_value != null) totalMargin += r.margin_value
      if (r.revenue_window != null) totalRevenue += r.revenue_window

      // Capital parado: tem estoque, custo, e giro baixo
      if (stockVal > 0 && (r.monthly_turnover ?? 0) < LOW_TURNOVER_THRESHOLD) {
        capitalParado += stockVal
        capitalParadoCount++
      }

      // ROI por produto: margin_value / (stock × cost_avg)
      if (r.margin_value != null && r.margin_value > 0 && stockVal > 0) {
        roiSum += (r.margin_value / stockVal) * 100
        roiCount++
      }

      if (r.abc_curve && r.abc_curve in abcFinanceiro) {
        abcFinanceiro[r.abc_curve].value += stockVal
        if (r.margin_value != null) abcFinanceiro[r.abc_curve].margin += r.margin_value
        if (r.revenue_window != null) abcFinanceiro[r.abc_curve].revenue += r.revenue_window
      }
    }

    // GMROI = margem bruta total / custo médio do estoque
    // (usando custo total em estoque como proxy do custo médio no período)
    const gmroi = totalValue > 0 ? totalMargin / totalValue : null

    // Margem ponderada global
    const avgMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : null

    // ROI médio do estoque
    const avgRoi = roiCount > 0 ? roiSum / roiCount : null

    // Capital parado % do total
    const capitalParadoPct = totalValue > 0 ? (capitalParado / totalValue) * 100 : 0

    // Margem por curva ABC
    const abcMarginPct = Object.fromEntries(
      Object.entries(abcFinanceiro).map(([k, v]) => [
        k, v.revenue > 0 ? (v.margin / v.revenue) * 100 : null
      ])
    ) as Record<string, number | null>

    // CMV do período = receita − margem (margem = receita − custo)
    const cmv = totalRevenue - totalMargin
    // CMV como % da receita (= custo sobre faturamento; complemento da margem)
    const cmvPct = totalRevenue > 0 ? (cmv / totalRevenue) * 100 : null

    return {
      totalValue, totalMargin, totalRevenue, cmv, cmvPct,
      gmroi, avgMarginPct, avgRoi,
      capitalParado, capitalParadoCount, capitalParadoPct,
      abcFinanceiro, abcMarginPct,
    }
  }, [rows])

  // Metas dos KPIs (régua única → tom do card + selo). Limiares vêm do texto "analysis".
  const gmroiMeta = kpiMeta({ value: stats.gmroi, sentido: 'min', verde: 1.5, amarelo: 0.8, format: v => `${v}×` })
  const capitalMeta = kpiMeta({ value: stats.capitalParadoPct, sentido: 'max', verde: 10, amarelo: 25, format: v => `${v}%` })
  const margemMeta = kpiMeta({ value: stats.avgMarginPct, sentido: 'min', verde: 30, amarelo: 15, format: v => `${v}%` })
  const roiMeta = kpiMeta({ value: stats.avgRoi, sentido: 'min', verde: 50, amarelo: 20, format: v => `${v}%` })
  const cmvMeta = kpiMeta({ value: stats.cmvPct, sentido: 'max', verde: 70, amarelo: 85, format: v => `${v}%` })

  // Card ativo dirige a tabela detalhada (default: capital parado).
  const [activeCard, setActiveCard] = useState<'capital' | 'margem' | 'roi' | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  function selectCard(key: 'capital' | 'margem' | 'roi') {
    const next = activeCard === key ? null : key
    setActiveCard(next)
    if (next) tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const list = useMemo(() => {
    const enrich = (r: EstoqueRow) => ({ ...r, stockVal: (r.current_stock ?? 0) * (r.cost_avg ?? 0) })
    if (activeCard === 'margem') {
      return {
        title: 'Ordenado por margem (maior primeiro)',
        rows: rows.filter(r => r.margin_pct != null).map(enrich).sort((a, b) => (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity)),
      }
    }
    if (activeCard === 'roi') {
      return {
        title: 'Ordenado por ROI (maior primeiro)',
        rows: rows.map(enrich).filter(r => r.stockVal > 0 && (r.margin_value ?? 0) > 0)
          .sort((a, b) => (b.margin_value! / b.stockVal) - (a.margin_value! / a.stockVal)),
      }
    }
    return {
      title: `Capital parado — maiores posições (giro < ${LOW_TURNOVER_THRESHOLD} un/mês)`,
      rows: rows.map(enrich).filter(r => r.stockVal > 0 && (r.monthly_turnover ?? 0) < LOW_TURNOVER_THRESHOLD)
        .sort((a, b) => b.stockVal - a.stockVal),
    }
  }, [activeCard, rows])

  return (
    <div className="flex flex-col gap-6">
      {/* ── KPIs rápidos ──────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        <KpiCard
          label="GMROI"
          value={stats.gmroi != null ? `${stats.gmroi.toFixed(2)}×` : '—'}
          tone={gmroiMeta.tone}
          meta={{ status: gmroiMeta.status, label: gmroiMeta.metaLabel }}
          hint="margem bruta ÷ valor em estoque"
          analysis="Margem gerada por R$ em estoque. ≥1,5× bom · 0,8–1,5× atenção · <0,8× ruim."
        />
        <KpiCard
          label="Capital parado"
          value={fmtBRL(stats.capitalParado)}
          tone={capitalMeta.tone}
          meta={{ status: capitalMeta.status, label: capitalMeta.metaLabel }}
          hint={`${fmtPct(stats.capitalParadoPct)} do estoque · clique para listar`}
          onClick={() => selectCard('capital')}
          active={activeCard === 'capital'}
          analysis="R$ preso em itens de giro baixo. <10% do estoque bom · 10–25% atenção · >25% crítico."
        />
        <KpiCard
          label="Margem de contribuição"
          value={stats.avgMarginPct != null ? fmtPct(stats.avgMarginPct) : '—'}
          tone={margemMeta.tone}
          meta={{ status: margemMeta.status, label: margemMeta.metaLabel }}
          hint="clique para ordenar"
          onClick={() => selectCard('margem')}
          active={activeCard === 'margem'}
          analysis="% que sobra após o custo, ponderada pelo faturamento. ≥30% bom · 15–30% atenção · <15% baixo."
        />
        <KpiCard
          label="ROI médio do estoque"
          value={stats.avgRoi != null ? fmtPct(stats.avgRoi, 0) : '—'}
          tone={roiMeta.tone}
          meta={{ status: roiMeta.status, label: roiMeta.metaLabel }}
          hint="clique para ordenar"
          onClick={() => selectCard('roi')}
          active={activeCard === 'roi'}
          analysis="Retorno médio por produto sobre o capital investido. ≥50% bom · 20–50% atenção · <20% baixo."
        />
        {/* Receita: card informativo (total em R$, sem meta) → sem selo. */}
        <KpiCard
          label="Receita no período"
          value={fmtBRL(stats.totalRevenue)}
          tone="success"
          hint={`vendas no período (${periodLabel})`}
          analysis={`Faturamento no período selecionado — ${periodLabel} (a preço de venda). Base da margem, do CMV e do giro.`}
        />
        <KpiCard
          label="CMV (período)"
          value={fmtBRL(stats.cmv)}
          tone={cmvMeta.tone}
          meta={{ status: cmvMeta.status, label: cmvMeta.metaLabel }}
          hint={stats.cmvPct != null ? `${fmtPct(stats.cmvPct)} da receita (${periodLabel})` : `custo das mercadorias vendidas (${periodLabel})`}
          analysis="Custo das vendas como % da receita (complemento da margem). ≤70% bom · 70–85% atenção · >85% ruim."
        />
      </div>

      {/* ── Detalhamento ─────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">

        {/* Margem por curva ABC */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Margem por curva ABC</h3>
          <BarChart
            horizontal
            bars={(['A', 'B', 'C'] as const).map(k => ({
              label: `Curva ${k}`,
              value: stats.abcMarginPct[k] ?? 0,
              color: k === 'A' ? '#22d3ee' : k === 'B' ? '#34d399' : '#818cf8',
            }))}
            formatValue={v => fmtPct(v)}
          />
          <p className="text-[11px] text-ink-faint">Margem de contribuição (%) por curva</p>
        </section>

        {/* Valor em estoque por curva */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Capital por curva ABC</h3>
          <BarChart
            horizontal
            bars={(['A', 'B', 'C'] as const).map(k => ({
              label: `Curva ${k}`,
              value: stats.abcFinanceiro[k].value,
              color: k === 'A' ? '#22d3ee' : k === 'B' ? '#34d399' : '#818cf8',
            }))}
            formatValue={v => fmtBRL(v)}
          />
          <p className="text-[11px] text-ink-faint">Capital imobilizado (stock × custo médio) por curva</p>
        </section>

        {/* Composição do estoque */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Composição financeira</h3>
          <div className="flex flex-col gap-2 text-sm">
            {[
              { label: 'Valor total em estoque', value: fmtBRL(stats.totalValue), color: 'text-sky-400' },
              { label: 'Receita no período', value: fmtBRL(stats.totalRevenue), color: 'text-emerald-400' },
              { label: 'Margem bruta total', value: fmtBRL(stats.totalMargin), color: 'text-brand-300' },
              { label: 'Capital parado', value: fmtBRL(stats.capitalParado), color: 'text-orange-400' },
            ].map(item => (
              <div key={item.label} className="flex justify-between items-center">
                <span className="text-ink-muted text-xs">{item.label}</span>
                <span className={`font-semibold tabular-nums ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Lista dirigida pelo card ativo (default: capital parado) */}
        <section ref={tableRef} className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3 md:col-span-2 lg:col-span-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{list.title}</h3>
            {activeCard && (
              <button
                onClick={() => setActiveCard(null)}
                className="text-[11px] px-2 py-0.5 rounded bg-sunken hover:bg-line text-ink-muted border border-line"
              >
                Limpar
              </button>
            )}
          </div>
          {list.rows.length === 0 ? (
            <p className="text-sm text-emerald-400">Nenhum item nesta lista.</p>
          ) : (
            <div className="overflow-y-auto max-h-[420px]">
              <table className="w-full text-sm mobile-stack-table">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                    <th className="pb-2 font-medium">Produto</th>
                    <th className="pb-2 font-medium text-right">Saldo</th>
                    <th className="pb-2 font-medium text-right">Custo un.</th>
                    <th className="pb-2 font-medium text-right">Capital</th>
                    <th className="pb-2 font-medium text-right">Giro/mês</th>
                    <th className="pb-2 font-medium text-right pr-3">Margem %</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeCard ? list.rows : list.rows.slice(0, 10)).map(r => (
                    <tr key={r.id} className="border-b border-line/50 last:border-0">
                      <td className="py-1.5 pr-4 text-ink max-w-[200px] truncate">{r.name}</td>
                      <td className="py-1.5 pr-4 text-right text-ink-muted">{fmtNum(r.current_stock)}</td>
                      <td className="py-1.5 pr-4 text-right text-ink-muted">{r.cost_avg != null ? fmtBRL(r.cost_avg, { decimals: 2 }) : '—'}</td>
                      <td className="py-1.5 pr-4 text-right font-semibold text-orange-400">{fmtBRL(r.stockVal)}</td>
                      <td className="py-1.5 pr-4 text-right text-ink-muted">{r.monthly_turnover != null ? fmtNum(r.monthly_turnover, 1) : '—'}</td>
                      <td className="py-1.5 pr-3 text-right">
                        {r.margin_pct != null ? (
                          <span className={r.margin_pct >= 20 ? 'text-emerald-400' : r.margin_pct >= 10 ? 'text-yellow-400' : 'text-red-400'}>
                            {fmtPct(r.margin_pct)}
                          </span>
                        ) : <span className="text-ink-faint">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!activeCard && list.rows.length > 10 && (
                <p className="text-[11px] text-ink-faint mt-2">+{list.rows.length - 10} itens adicionais</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
